import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLayout } from 'hooks/useLayout'
import { Modal } from 'components/Modal/Modal'
import { useSnackbar } from 'stores/useSnackbar'
import { type StateFileUpload } from 'types/fileUpload'
import { Box, Button, Typography } from '@mui/material'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { UploaderDisplayer } from 'components/FileUploader/UploaderDisplayer'
import { ActivityButton } from 'components/ActivityButton'
import { usePostCelebrations } from 'hooks/api/Settings/usePostCelebration'
import { type CelebrationType } from './SettingsDetailsCelebrations'
import { usePutCelebrations } from 'hooks/api/Settings/usePutCelebration'

export const UploadAnimationFile = ({
  celebrationAnimationId,
  celebrationAnimationType,
  celebrationAnimationDescription,
  isModalOpen,
  toggleModalIsOpen,
  onAnimationRefresh,
}: {
  celebrationAnimationId: number
  celebrationAnimationType: CelebrationType
  celebrationAnimationDescription: string
  isModalOpen: boolean
  toggleModalIsOpen: () => void
  onAnimationRefresh: (src: string) => void
}) => {
  const setSnackbarMessage = useSnackbar((state) => state.setMessage)
  const [files, setFiles] = useState<File[]>([])
  const [showFiles, setShowFiles] = useState(false)
  const [stateFilesUpload, setStateFilesUpload] = useState<StateFileUpload>(0)
  const [messageError, setMessageError] = useState('')
  const { isMobile } = useLayout()

  const { handleSubmit } = useForm()

  const toggleShowFiles = () => setShowFiles(!showFiles)
  const toggleStateFilesUpload = (value: StateFileUpload) => {
    setMessageError('')
    if (stateFilesUpload === 0) {
      setStateFilesUpload(value)
    }
    if (value === 2) {
      setMessageError('Please correct errors to continue')
    }
  }

  const removeFile = (file: File) => {
    setFiles(
      files?.filter((fileMatch) => {
        return fileMatch.name !== file.name
      })
    )
    setStateFilesUpload(0)
    toggleShowFiles()
  }

  const addFiles = (files: File[]) => {
    setFiles(files)
    toggleShowFiles()
  }

  const resetAll = () => {
    toggleModalIsOpen()
    setShowFiles(false)
    setFiles([])
    setMessageError('')
  }

  const { mutate: saveFileMutation, isPending: isUploadSaveActive } =
    usePostCelebrations({
      onSuccess: () => {
        // Immediate refresh with local object URL
        if (files[0]) {
          const newSrc = URL.createObjectURL(files[0])
          onAnimationRefresh(newSrc)
        }
        resetAll()
        setSnackbarMessage(`Animation file has been saved`)
      },
      onError: (error, _) => {
        console.log(error)
        setMessageError(error.message)
      },
    })

  const { mutate: updateFileMutation, isPending: isUploadUpdateActive } =
    usePutCelebrations({
      id: celebrationAnimationId,
      onSuccess: () => {
        // Immediate refresh with local object URL
        if (files[0]) {
          const newSrc = URL.createObjectURL(files[0])
          onAnimationRefresh(newSrc)
        }
        resetAll()
        setSnackbarMessage(`Animation file has been updated`)
      },
      onError: (error, _) => {
        console.log(error)
        setMessageError(error.message)
      },
    })

  const updateAnimationFileWrapper = handleSubmit(async () => {
    if (stateFilesUpload !== 1) {
      setMessageError('Please correct errors to continue')
      return
    }

    if (celebrationAnimationId === 0) {
      saveFileMutation({
        Id: 0,
        Type: celebrationAnimationType,
        Description: celebrationAnimationDescription as CelebrationType,
        FileName: files[0].name,
        AnimationFile: files[0],
      })
    } else {
      updateFileMutation({
        Id: celebrationAnimationId,
        Type: celebrationAnimationType,
        Description: celebrationAnimationDescription as CelebrationType,
        FileName: files[0].name,
        AnimationFile: files[0],
      })
    }
  })

  return (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '60%',
        height: isMobile ? '100%' : '75%',
      }}
    >
      <Box>
        <form onSubmit={updateAnimationFileWrapper}>
          <ModalFullHeader
            toggleIsOpen={toggleModalIsOpen}
            title={`Animation File for ${celebrationAnimationDescription}`}
          />
          <ModalFullBody heightModal={isMobile ? '100%' : '75%'}>
            {messageError && (
              <Box className="w-full mb-[3%]">
                <FormFieldErrorMessage
                  message={messageError}
                  variant="contained"
                />
              </Box>
            )}
            <Typography variant="subtitle-2" className="w-full">
              Upload Animation
            </Typography>
            <UploaderDisplayer
              files={files}
              addFiles={addFiles}
              removeFile={removeFile}
              toggleStateFilesUpload={toggleStateFilesUpload}
              showImages={showFiles}
              textToShowButton="Click to upload."
              informationFileUpload="Saving will overwrite any existing animation."
              maxSizeAllowedUpload={500}
              fileFormatsAllowedUpload="application/json"
            />
          </ModalFullBody>
          <ModalFullFooter>
            <Button onClick={resetAll}>Cancel</Button>
            <ActivityButton
              type="submit"
              variant="contained"
              active={isUploadSaveActive || isUploadUpdateActive}
            >
              Save
            </ActivityButton>
          </ModalFullFooter>
        </form>
      </Box>
    </Modal>
  )
}
